import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";
import { useDispatch, useSelector, connect } from "react-redux";
import { reduxForm, Field, initialize, formValueSelector } from "redux-form";
import { openModal, closeModal, fetchError } from "../../actions";
import { Form, Button, Grid, Menu, Container, Header } from "semantic-ui-react";
import { TextInput, FileInput, AdvertiserOptions } from "../utils/form-fields";
import { createBanner, updateBanner, uploadFile } from "../../firebase";
import { CancelButton } from "../utils/buttons";
import Loading from "../../app/loading";

let templateFields = [];

let KevelForm = ({
  pristine,
  submitting,
  handleSubmit,
  retailer,
  change,
  invalid,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const template = useSelector((state) => state.template);
  const banner = useSelector((state) => state.banner);
  const user = useSelector((state) => state.user);
  const advertisers = useSelector((state) => state.advertisers);

  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    if (template.fields) {
      banner.fields = banner.fields || {};
      templateFields = [...template.fields];
      dispatch(initialize("kevelForm", banner));
    }
  }, [dispatch, banner, template]);

  const getAdvertiser = (advertiser) => {
    return _.find(advertisers, (item) => {
      return item.displayName === advertiser;
    });
  };

  const handleFileUpload = (file, key, title) => {
    let folder = banner.key || key;
    if (file.type.indexOf("image/") === -1) {
      return dispatch(
        fetchError({
          name: "Upload error",
          error: "Wrong file type. Please upload an image - jpg, gif or png",
        })
      );
    }
    uploadFile(file, folder, (data) => {
      dispatch(
        openModal("ProgressModal", {
          size: "tiny",
          progress: data.progress,
          name: file.name,
          title,
          centered: false,
        })
      );
      if (data.progress === 100) {
        change(key, data.path);
        //window.setTimeout(() => dispatch(closeModal()), 1000);
        dispatch(closeModal());
      }
      if (data.error) {
        dispatch(
          openModal("ErrorModal", {
            size: "tiny",
            centered: false,
            error: {
              name: file.name,
              error: "Image error",
              reason: "File size too large or dimensions are wrong",
            },
          })
        );
      }
    });
  };

  const handleSubmitForm = (values) => {
    setShowLoading(true);
    const { advertiserId } = getAdvertiser(values.advertiser);
    const currentUser = `${user.displayName} (${user.organisation})`;
    const submitValues = {
      ...values,
      bannerType: banner.bannerType || template.name,
      currentUser: false,
      retailer,
      advertiserId,
      templateId: template.id,
      bespokeTemplate: false,
      templateType: template.type,
      service: template.service,
      lastModBy: currentUser,
    };

    banner.key
      ? updateBanner(submitValues, banner.key, dispatch).then(() => {
          setShowLoading(false);
        })
      : createBanner(submitValues, dispatch)
          .then((message) => {
            if (message.id) navigate(`/${retailer}/banner/${message.id}`);
            console.log(message);
          })
          .catch((error) => console.log(error));
  };

  if (showLoading) return <Loading />;

  return (
    <Form onSubmit={handleSubmit(handleSubmitForm)}>
      <Grid padded className="trade-form">
        <Grid.Row>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Banner info</Header>
            <Field
              name="bannerTitle"
              label="Title"
              type="text"
              component={TextInput}
              placeholder="Add a banner title"
              isRequired={true}
            />
            <Field
              name="adNumber"
              label="Ad number"
              type="text"
              component={TextInput}
              placeholder="Add an ad number"
              isRequired={true}
            />
            <div className="field">
              <div className="field">
                <label htmlFor="advertiser">Advertiser</label>
                <Field
                  name="advertiser"
                  component="select"
                  multiple={false}
                  className="ui selection dropdown"
                >
                  {!user.restrictByAdvertiser && (
                    <option value="">Choose an advertiser</option>
                  )}
                  <AdvertiserOptions advertisers={advertisers} />
                </Field>
              </div>
            </div>
            <Field
              name="url"
              label="Banner link"
              type="text"
              component={TextInput}
              placeholder="Add a URL link"
              isRequired={true}
            />
            <Field
              name="adTypeId"
              label="Ad Type Id"
              type="text"
              component={TextInput}
              placeholder="Add an ad type id"
              isRequired={true}
            />
          </Grid.Column>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Template fields</Header>
            {_.map(
              template.fields,
              (field) =>
                field.type === "String" && (
                  <Field
                    label={field.name}
                    name={`fields.${field.variable}`}
                    key={`fields.${field.variable}`}
                    type="text"
                    component={TextInput}
                    isRequired={field.required}
                    placeholder={field.description}
                  />
                )
            )}
          </Grid.Column>
          <Grid.Column computer={4} tablet={8} mobile={16}>
            <Header as="h3">Images</Header>
            {_.map(
              template.fields,
              (field) =>
                field.type === "File" && (
                  <div className="field" key={field.variable}>
                    <label>{field.name}</label>
                    {fieldValues[field.variable] && (
                      <img src={fieldValues[field.variable]} alt={field.name} />
                    )}
                    <Field
                      name={`fields.${field.variable}`}
                      component={FileInput}
                      fileName={field.variable}
                      callback={(file) =>
                        handleFileUpload(
                          file,
                          `fields.${field.variable}`,
                          field.name
                        )
                      }
                      handleFileRemove={(e) =>
                        change(`fields.${field.variable}`, {})
                      }
                    />
                  </div>
                )
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Menu fixed="bottom" size="mini">
        <Container>
          <Menu.Item>
            <Button
              disabled={submitting || pristine || invalid || showLoading}
              positive
              type="submit"
              loading={showLoading}
              size="mini"
            >
              Save
            </Button>
          </Menu.Item>
          <Menu.Item>
            <CancelButton
              id={banner.key}
              location={location}
              retailer={retailer}
              pristine={pristine}
              size="mini"
            />
          </Menu.Item>
        </Container>
      </Menu>
    </Form>
  );
};

const validate = (values) => {
  const isUrlValid = (url) => {
    return url.match(
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
    );
  };

  const hasWhiteSpace = (hex) => {
    return hex.match(/\s/g);
  };

  const errors = { fields: {} };

  if (!values.bannerTitle) {
    errors.bannerTitle = "Please enter a title";
  }
  if (!values.adNumber || isNaN(Number(values.adNumber))) {
    errors.adNumber = "Please enter a number only";
  }
  if (!values.advertiser) {
    errors.advertiser = "Please select an advertiser";
  }
  if (values.bannerURL) {
    if (!isUrlValid(values.bannerURL)) {
      errors.bannerURL = "Please enter a valid URL";
    }
    if (isUrlValid(values.bannerURL)) {
      if (hasWhiteSpace(values.bannerURL)) {
        errors.bannerURL = "Please remove any spaces";
      }
    }
  }

  templateFields.forEach((field) => {
    if (field.required) {
      if (values.fields) {
        if (!values.fields[field.variable]) {
          errors.fields[field.variable] = "This is a required field";
        }
      }
    }
  });

  return errors;
};

KevelForm = reduxForm({
  validate,
  form: "kevelForm",
})(KevelForm);

const selector = formValueSelector("kevelForm");
let fieldValues = {};

KevelForm = connect((state) => {
  const advertiserValue = selector(state, "advertiser");
  if (state.template.fields) {
    state.template.fields.map((field) => {
      return (fieldValues = {
        ...fieldValues,
        [field.variable]: selector(state, `fields.${field.variable}`),
      });
    });
  }
  return {
    advertiserValue,
    fieldValues,
  };
})(KevelForm);

export default KevelForm;
